import {
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBrandAndroid, IconBrandApple, IconDownload } from '@tabler/icons-react';
import { useState } from 'react';

import { api, apiOrigin } from '../api';
import { apiHook } from '../treaty';

const platformIcons = {
  android: IconBrandAndroid,
  macos: IconBrandApple,
};

function formatBytes(bytes: number | null) {
  if (bytes === null) return null;
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

export function DownloadsPage() {
  const releases = apiHook.useQuery((client) => client['app-downloads'].get());
  const [downloading, setDownloading] = useState<string | null>(null);

  async function download(platform: string, downloadUrl: string, fileName: string) {
    setDownloading(platform);
    try {
      const response = await fetch(`${apiOrigin}${downloadUrl}`, {
        headers: api.authorizationHeaders,
      });
      if (!response.ok) throw new Error(`Download failed with status ${response.status}`);

      const objectUrl = URL.createObjectURL(await response.blob());
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Download failed',
        message: err instanceof Error ? err.message : 'Could not download this installer.',
      });
    } finally {
      setDownloading(null);
    }
  }

  return (
    <Stack gap='lg' maw={1100} mx='auto'>
      <div>
        <Title order={1}>App downloads</Title>
        <Text c='dimmed' mt={5}>
          Install Disit Internal on Android or Disit Drive on your Mac.
        </Text>
      </div>

      <Alert color='yellow' title='Internal builds'>
        These installers are intended for company devices. macOS and Windows may display a security
        warning because the macOS installer is not code-signed.
      </Alert>

      {releases.isError && (
        <Alert color='red' title='Could not load downloads'>
          {releases.error.message}
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing='lg'>
        {releases.data?.map((release) => {
          const PlatformIcon = platformIcons[release.platform];
          return (
            <Paper key={release.platform} withBorder radius='lg' p='xl' className='data-card'>
              <Stack h='100%' gap='lg'>
                <Group justify='space-between' align='flex-start'>
                  <ThemeIcon size={48} radius='md' variant='light'>
                    <PlatformIcon size={27} />
                  </ThemeIcon>
                  <Badge color={release.available ? 'green' : 'gray'} variant='light'>
                    {release.available ? 'Available' : 'Not published'}
                  </Badge>
                </Group>
                <div>
                  <Title order={3}>{release.title}</Title>
                  <Text c='dimmed' size='sm' mt={6}>
                    {release.description}
                  </Text>
                </div>
                <Group gap='xs'>
                  <Badge variant='light'>v{release.version}</Badge>
                  <Badge variant='default'>{release.format}</Badge>
                  <Badge variant='default'>{release.architecture}</Badge>
                  {release.available && (
                    <Badge variant='default'>{formatBytes(release.size)}</Badge>
                  )}
                </Group>
                <Button
                  mt='auto'
                  leftSection={<IconDownload size={18} />}
                  disabled={!release.available}
                  loading={downloading === release.platform}
                  onClick={() =>
                    void download(release.platform, release.downloadUrl, release.fileName)
                  }
                >
                  {release.available ? `Download ${release.format}` : 'Not available'}
                </Button>
              </Stack>
            </Paper>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
}
